/**
 * For more details, refer to the documentation: [https://www.notion.so/reservamos/Feature-Flags-75c6bb137ef2416ca8e36f03fd042fca?pvs=4]
 *
 * @todo Some Purchase API whitelabel feature flags are not represented here yet.
 * Add them as their consumers migrate to TypeScript so `WhitelabelFeatures` covers the
 * Purchase API `feature_flags` contract. GrowthBook experiment flags are loaded independently
 * and do not belong in this object.
 */

const defaultFeatures = {
  // Which of the three after-midnight dividers renders. Presentation only: it does NOT decide
  // whether the section shows, which is the presence of trips in the payload.
  AFTER_MIDNIGHT_DIVIDER_VARIANT: 'prominent' as 'prominent' | 'soft' | 'minimal',
  BILLING_ADDRESS_ENABLED: false,
  // Document types (brand values) that may resolve or create a billing company, e.g. ['NIT'].
  // Empty disables the whole billing-company flow: any other document type is ignored.
  BILLING_COMPANY_AVAILABLE_DOCUMENTS: [] as string[],
  CALENDAR_RANGE: 90,
  DISCOUNT_CODE_ENABLED: false,
  CHECKOUT_ACCOUNT_ACTIVATION: false,
  QUICK_PURCHASE_ENABLED: false,
  // Quick Purchase assigns a seat without reading its price, so editing it from the quick checkout
  // is the buyer's remedy — and it only works as one while it keeps them inside that screen. When
  // this is off, the edit-seats button navigates to /purchase/:token/seats/:way instead of swapping
  // the selector in place. `false` is what every tenant already resolved before this key existed
  // (`useExperimentalFeature` fell through to an absent key), so declaring it changes no behavior;
  // turning in-place editing on stays a per-brand rollout decision, made in GrowthBook.
  QUICK_PURCHASE_SEAT_EDIT_ENABLED: false,
  SMART_SEAT_SELECTION_FOR_QUICK_PURCHASE_ENABLED: false,
  // The seat-edit remedy above only helps a buyer who can tell there is something to correct, so on
  // a tenant with per-seat pricing the quick checkout shows what each assigned seat costs. The
  // figure comes from the purchase tickets (`amount + taxes`), the same source the standard checkout
  // reads, so the two screens cannot quote different prices for one seat. `false` by default: the
  // tenants already live price every seat on a bus alike, where the row would repeat one number on
  // every card instead of informing. Turning it on is a per-brand decision, made in GrowthBook.
  QUICK_PURCHASE_SEAT_AMOUNT_ENABLED: false,
  PROMOTIONAL_CODE_ENABLED: false,
  FUNNEL_STYLE: 'CLASSIC' as 'CLASSIC' | 'FLAT', // CLASSIC | FLAT
  IDENTIFICATION_DOCUMENT_PER_PASSENGER: false,
  IDENTIFICATION_SECOND_DOCUMENT_PER_PASSENGER: false,
  IDENTIFICATION_DOCUMENT_FOR_PURCHASER: false,
  INSTALLMENTS_ENABLED: false,
  // Ask for the account on the payment screen instead of before the passengers form. Named as in
  // saas-app, so one word describes the same behaviour in both clients. Gontijo only: with it off
  // the Gontijo session is never read, written or attached to any payload.
  LOGIN_ON_CHECKOUT: false,
  LOADING_TYPE: 'DEFAULT' as 'DEFAULT' | 'DOTS', // DEFAULT | DOTS
  LODGING_CHECKBOX_ENABLED: false,
  PASSENGERS_INSURANCE_ENABLED: false,
  PASSENGERS_TERMS_AND_CONDITIONS_ENABLED: false,
  PURCHASE_TIMER_ENABLED: true,
  PURCHASER_FORM_ON: 'PASSENGERS' as 'PASSENGERS' | 'CHECKOUT', // PASSENGERS | CHECKOUT
  PRICE_CHANGED_MODAL_ENABLED: false,
  PRINT_TICKETS_REQUIRED: true,
  PRIVACY_BANNER_ENABLED: false,
  PROVIDERS_SELECTION_ENABLED: true,
  OPEN_TICKET_PROVIDER_SELECTION_ENABLED: false,
  SEARCH_SIDEBAR_ENABLED: false,
  PREVENT_OVERSELECT_ON_RETURN_TRIP: false, // If true, prevents deselecting the oldest seat when adding a new primary seat on a return trip
  SEAT_SELECTION_LIMIT: 5,
  ROUND_SEAT_SELECTION_LIMIT: false as false | number,
  SHOW_RECOMENDATIONS: false,
  SHOW_SERVICE_TYPE: true,
  SUPPORT_FOR_ORTHOGRAPHIC_ACCENTS_ON_PASSENGERS: true,
  STORE_PURCHASER_FORM: true,
  STORE_PASSENGERS_FORM: true,
  SHOW_PURCHASER_EMAIL_FIELD: true,
  SHOW_PURCHASER_SECOND_LAST_NAME_FIELD: false,
  SHOW_TERMINAL_NAME_AT_ITINERARY_ITEM: true,
  LOW_AVAILABILITY_THRESHOLD: 5,
  // Shows remaining ticket availability for dynamic-pricing and early discounts.
  // Set to false per brand to hide the label.
  SHOW_DYNAMIC_PRICING_AVAILABILITY_LABEL: true,
  VALID_CATEGORIES: [] as string[],
  COUPON_ON_SEARCH: false,
  USE_HORIZONTAL_RESULTS: false,
  SEARCH_CITY_REDUNDANT: false,
  SHOW_HEADER_LABEL: true,
  MULTI_OPEN_TICKETS: false,
  NEW_PROVIDER_RESULTS: false,
  NEW_RESULTS_DESIGN: false, // If true, uses the new results design with simplified trip items and new layout
  SCHEDULE_RESULT_TYPE: 'default' as
    | 'default'
    | 'GFA'
    | 'specifics'
    | 'horizontal'
    | 'icons'
    | 'result'
    | 'simpleResult',
  SHOW_OPEN_TICKET_ON_PROVIDERS: false,
  SHOW_SEARCH_FOOTER: false,
  SHOW_PURCHASE_FOOTER: true,
  SHOW_PURCHASE_REVIEW_TABS: false,
  SHOW_SIDEBAR_MENU: false,
  ALLOW_TO_CHANGE_LANGUAGE: false,
  ALLOW_TO_SAVE_CARDS: false,
  USE_LEGACY_LOCK_TICKETS: false,
  PASSENGERS_EXTRA_FIELDS: false,
  PASSENGERS_SHOW_ID: false,
  VALIDATE_ROUND_TRIP_DISCOUNT: false,
  TRIP_PRICING_BY_PASSENGER: true,
  // If true, the per-passenger pricing (TRIP_PRICING_BY_PASSENGER) itemizes the fare
  // breakdown (toll, boarding tax, management costs) so the lines add up to the passenger
  // total, like the PurchasePricingTrip layout. If false, the fare is shown lumped as subtotal.
  SHOW_PASSENGER_PRICING_BREAKDOWN: false,
  SHOW_MIN_PRICES: false,
  SHOW_TICKET_PRICE_WITHOUT_TAX: false,
  SHOW_ENHANCED_TICKET_DATA: false, // If true, shows enhanced ticket data like transporter key and seat number in the ticket list of exchange tickets
  TRIP_REDIRECTION_LEAD: false,
  TRIP_REDIRECTION_RECEIVE: false,
  SHOW_SCHEDULE_CHANGES_TEXT: false,
  SHOW_SORT_PROPERTY: false,
  SHOW_OPEN_TICKET_ON_RESULTS: false,
  OPEN_TICKET_MODAL_ENABLED: false,
  SHOW_DYNAMIC_PROMOTIONS: false,
  GFA_INSURANCE_CHECKBOX: false,
  INSURANCE_ON_CHECKOUT_ONLY: false, // If true, the insurance will only be shown on checkout screen
  SHOW_ADYEN_CARD_LOGOS: true,
  PERSIST_SEATS_ON_RECEIVE_PURCHASE: false,
  WOMAN_SEATS_ENABLED: false,
  RETURN_TRIP_REMINDER: false,
  PURCHASE_PRICING_AGGREGATION_ENABLED: false,
  PURCHASE_PRICING_DETAILED_DISCOUNT: false,
  SORT_TRIPS_BY_PRICE: false,
  SHOW_PASSENGERS_SECOND_LAST_NAME_FIELD: true,
  SHOW_PASSENGERS_EMAIL_FIELD: true,
  CAN_USE_POINTS: false,
  ALLOW_TO_EXCHANGE_TICKETS: false,
  USE_EXCHANGE_TICKET_LIST_ROUTE: false,
  USE_NERBY_TERMINALS: false,
  EXCHANGE_PASSENGERS_EDITABLE: false,
  CAN_ADD_EXCHANGE_INSURANCE: false,
  CAN_EXCHANGE_CHANGE_PLACES: false,
  USE_SEARCH_WIDGET_ON_EXCHANGE: false,
  OPENPAY_REGION: 'mexico',
  CAN_EXCHANGE_OPEN_TICKET: false,
  VALIDATE_LOYALTY_ON_EXCHANGE: [] as string[],
  DISABLE_EXCHANGE_PURCHASER: false,
  HIDE_EXCHANGE_PURCHASER_FORM: false, // If true, hides the purchaser form completely on exchange tickets
  SHOW_SERVICE_TYPE_BADGE: false,
  SHOW_TRIP_DETAILS_AT_RESULTS: false,
  SHOW_DISCOUNTS_MODAL_AT_RESULTS: false,
  DOWNLOAD_TICKETS_PAGE_ENABLED: false,
  REMOTE_TICKETS: false,
  BIRTH_DATE_PER_PASSENGER: false,
  NATIONALITY_PER_PASSENGER: false,
  DOCUMENT_PER_PASSENGER: false, // Flag that indicates if the document field should be shown per passenger
  TICKETS_REFERENCE: 'transporter-key' as 'transporter-key' | 'purchase-token', // transporter-key | purchase-token
  HIDE_LOCATION_ON_PURCHASE_COMPLETE: false, // Flag that indicates if the location buttons should be hidden on the purchase complete screen
  SHOW_TRAVEL_TIPS_PURCHASE_COMPLETE: false, // Feature flag: if true, travel tips can show on purchase complete when route is in env.travelTipsAllowedRoutes
  HIDE_PRICE_DETAILS_TOOLTIP: false, // Flag that indicates if the tooltip containing the details of pricing shoul be hidden
  HIDE_PAYPAL_DEBIT_LOGOS: false, // Flag that indicates if the debit logos should be rendered on paypal form
  SHOW_ITINERARY_AT_TRIP_DETAILS: true,
  SHOW_BIRTH_DATE_BELOW_BUS_CATEGORIES: false,
  SHOW_BIRTH_DATE_EXCLUDING_ADULTS: false,
  SHOW_BIRTH_DATE_WARNING: false, // Flag that indicates if the birth date warning should be shown
  SHOW_DOCUMENT_EXCLUDING_ADULTS: false, // Flag that indicates if the document field should be shown excluding adults
  SHOW_PASSENGER_IS_BUYER: false,
  SHOW_CARD_ACTIVE_MEMO: false, // Flag that indicates if the memo to activate card should be visible
  USE_NEW_CARD_INPUT: false, // Flag that indicates if the card form should render the enhanced input with card format and brand recognize
  USE_NEW_CARD_STRUCTURE: false, // If true, uses unified expiration date (MM/YY) and sanitized card number in the card form
  PHONE_COUNTRY_SAVED_BACKEND: false,
  NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD: false, // Flag that indicates that the email should be send to the purchaser data even if it is not showed in the purchaser form
  NOT_WS_SANDBOX: false, // If true, show a checkout modal warning that purchases hit production (no WS sandbox)
  JUST_ADULT_OPEN_TICKET: false,
  SHOW_OPEN_TICKET_RESULT: false,
  COSTAPASS_ENABLED: false,
  DOTERS_ENABLED: false,
  CDS_POINTS_EARNING_ENABLED: false,
  DOTERS_FLEXIBLE_PASSENGER_MATCHING: [] as ('redeem' | 'reward')[], // Array with possible values: ['redeem'], ['reward']. If 'redeem' is present, allows editing name fields for Doters users during flexible redemption
  USE_SSO: false,
  AMENITIES_FIRST_AT_TRIP_DETAILS: false,
  PRICING_BEFORE_CHECKOUT: false,
  MIX_DISCOUNT_FARES_BY_AVAILABILITY: false, // Allows mixed fares when selected seats exceed discount availability.
  DISABLE_PASSENGER_LOGGED: false,
  SPECIAL_CATEGORIES: [] as string[],
  SPECIFIC_CATEGORIES: [] as string[], // If empty, shows all categories. If has elements, shows only the categories in the array
  LOYALTY_NEEDS_ACCOUNT_IN_PURCHASER: [] as string[],
  LOYALTY_NEEDS_ACCOUNT_IN_PASSENGERS: [] as string[],
  POLICIES_DISCLAIMER_LOCATION: 'PASSENGERS' as 'PASSENGERS' | 'CHECKOUT', // PASSENGERS | CHECKOUT
  SHOW_POWERED_BY: true,
  ROUND_CURRENCY_AMOUNTS: true,
  TOTAL_AT_THE_BOTTOM: false,
  SHOW_UNIFORM_LAYOUT: false,
  HALLWAY_SEAT_ALIGNMENT_ENABLED: false,
  SHOW_EXCHANGE_DISCLAIMER: true, // If true, the exchange disclaimer will be shown
  CIELO_BILLING_ADDRESS_ENABLED: false,
  /**
   * Array of object to use as filter. This allows costapass filter
   * @example:
   * {
   *  id: 'costapass',
   *  i18Key: 'search:trip_options.costapass',
   *  primaryText: 'search:trip_options.costapass',
   *   value: {
   *     availableWallets: 'costapass',
   *   },
   * },
   */
  TRIP_OPTIONS_FILTER: [] as {
    id: string;
    i18Key: string;
    primaryText: string;
    value: {
      availableWallets: string;
    };
  }[],
  BOOKING_REVIEW_ENABLED: false,
  DYNAMIC_CYBERSOURCE_CREDENTIALS: false,
  SHOW_OPEN_TICKET_BUTTON: true,
  DISCOUNT_LABEL_ROUND_METHOD_ON_RETURN: 'ROUND' as 'ROUND' | 'FLOOR' | 'CEIL', // ROUND | FLOOR | CEIL
  SHOW_ROUTE_LABEL: false, // If true, shows the route label (e.g., "Vía Xalapa") next to the schedule on trip results
  ROUTE_LABEL_CITIES: [] as string[], // Array of city names to highlight as route labels (e.g., ["Xalapa", "Cordoba"]); requires SHOW_ROUTE_LABEL: true
  SHOW_STOPS_FILTER: true,
  DEFAULT_PAYMENT_OPTION_FIRST: false, // If true, the default payment option will be the first one
  USE_TERMINAL_NAME_WITH_LOCATION: false,
  SHOW_OFFICE_PRICE: true,
  EXTRA_RESULT_LINES: [] as string[], // Bus lines that owns a route
  ALLOW_CROSS_LINE_ROUND_TRIPS: true, // If true, allows return trips from different lines within the same brand (ignores provider filter on round trips)
  ALLOW_ANY_LINE_ROUND_TRIPS: false, // If true, return trips on a round trip are not scoped to the departure line or transporter group; any line covering the route can be picked for the return (marketplace behavior). Takes precedence over ALLOW_CROSS_LINE_ROUND_TRIPS.
  SHOW_SECOND_INFANT_DISCLAIMER: false, // If true, the second infant disclaimer will be shown
  SHOW_CREDIT_DEBIT_LOGOS: false,
  SHOW_INSTALLMENTS_BANKS: true,
  BIN_VALIDATION_FOR_INSTALLMENTS_ENABLED: false, // If true, validates BIN to enable/disable monthly installments plan
  // If true, when a Cybersource card BIN is in the tenant list, shows a digital-card recommendation message
  SHOW_DIGITAL_CARD_RECOMMENDATION: false,
  SHOW_DISCOUNT_BANNER: false,
  SHOW_SPECIAL_DISCOUNT_BANNER: false, // If true, shows the Hot Sale / Festival de Viajes special discount banner on the results page (brands etn/gho/costa)
  SHOW_SPECIAL_DISCOUNT_ICON: false, // If true, shows the per-trip Hot Sale / Festival de Viajes icon on trips with a provider discount (brands etn/gho/costa). Independent of SHOW_SPECIAL_DISCOUNT_BANNER; shares the same end date and routes.
  SPECIAL_DISCOUNT_BANNER_END_DATE: '', // YYYY-MM-DD. End date (inclusive) of the special discount campaign; the banner/icon show from today until this date. Empty = no expiry.
  SPECIAL_DISCOUNT_BANNER_ROUTES: '', // Comma-separated origin-destination terminal code pairs where the campaign shows, e.g. "MEXN-AGUA,AGUA-MEXN". Direction-specific (A-B matches only A->B); add the reverse pair to also cover the return leg. Empty = all routes.
  PROVIDER_ANNIVERSARY_DISCOUNT_PERCENT: 0, // Discount percentage shown in the provider anniversary banner (e.g. 35). 0 = not set.
  CHANGE_RESULT_LAYOUT: false,
  RESULT_SHADOW_VARIANT: false,
  SHOW_DAYS_CAROUSEL: true, // If true, shows the alternative-days carousel on search results
  CALENDAR_AVAILABILITY_ENABLED: false, // If true, loads and shows calendar prices in DaysCarousel
  RECOMMENDED_TRIPS_ENABLED: false,
  RECOMMENDED_TRIPS_LIMIT: 3, // Number of recommended trips to show
  SHOW_EARLY_DISCOUNTS: false, // If true, shows early discount prices (office price) on trip results
  SHOW_URGENCY_DISCOUNT_UI: false, // Used to show the new discount's UI, for now show_discount_urgency from growthbook overrides the value for experimental period
  SHOW_DIGITAL_DISCOUNT_BANNER: false,
  SHOW_DIGITAL_DISCOUNT_BADGE: false,
  // If true, shows the -X% discount badge on results cards (introduced by EN-641). Brands that
  // want the badge set it explicitly. Coverage is partial — grep the flag name for the sites
  // that honor it.
  DISCOUNT_BADGE_RESULT_ENABLED: false,
  // If true, TripDiscountAdvice also shows the badge for digital discount types (single_trip_discount,
  // departure_on_round_trip_discount, single_on_round_trip_discount), e.g. round-trip GFA discounts
  SHOW_DISCOUNTS_ON_RESULTS_DETAILS: false,
  SHOW_CAPACITY_CATEGORIES: false, // If true, shows total seats (capacity) column by category in discounts modal. Used for Brazilian ANTT compliance.
  PQRS_FORM: false, // If true, the PQRS form for Ochoa will be available
  CAN_SWITCH_WALLET_PRICE: false,
  TERMINAL_INCLUDES_CITY_NAME: false,
  SHOW_RESULT_LINE_NAME: true,
  SHOW_VARIABLE_DEPARTURE_TIME: false,
  FLAT_FARE_CONTEXT: 'boleto_recompensa' as 'boleto_recompensa' | 'tarifa_exclusiva', // boleto_recompensa | tarifa_exclusiva
  DOTERS_AUTOMATIC_ENROLL: false,
  SHOW_ACCEPT_POLICIES_CHECKBOX: false, // If true, the accept policies checkbox will be shown
  SHOW_TICKET_REVIEW_MODAL: false, // If true, the ticket review modal will be shown for intermunicipal_free_pass passengers
  NATIONALITY_FIELD_WITH_FILTER: false, // If true, the nationality field will be shown with a filter
  PHONE_NUMBER_PER_PASSENGER: false, // If true, the phone number field will be shown per passenger
  GENDER_PER_PASSENGER: false, // If true, the gender field will be shown per passenger
  ENABLE_CATEGORIES: false as false | 'categories' | 'bar' | 'simple' | 'control', // Seat categories mode on results
  SEARCH_PASSENGERS: false, // If true, enables the passengers dropdown in the search widget and uses the new passengers URL format
  RESULTS_SEATS: false, // If true, enables seats preview on search results (requires SEATS_ON_RESULTS to also be true)
  SEATS_ON_RESULTS: false, // Enables the flow where the seats are selected on the results page
  SEAT_PRICE_TOTAL_MOBILE: false, // If true, shows the total seat price on mobile in the seat counter
  SECOND_FIRST_NAME_PER_PASSENGER: false, // If true, the second first name field will be shown per passenger
  HOUR_FORMAT: '12' as '12' | '24', // 12 | 24
  SUPPORT_TRIPS_WITH_CONNECTIONS: false,
  // When true, connection trips show one seat map and replicate the seat onto every leg.
  CONNECTION_SAME_SEAT: false,
  ALLOW_HYPHEN_IN_NAME_FIELDS: false, // If true, preserves hyphens only in primary first-name fields for passengers and purchaser
  ACCEPT_PURCHASER_BUSINESS_NAME: false,
  DOTERS_FLATFARE_VALIDATE_ROUND_TRIP: true, // If true, the round trip discount will be validated on return trip
  SHOW_TRIP_SERVICE_LABEL: false, // If true, the service will be shown in the results as comes from the backend
  ALLOW_CHANGE_CATEGORY_ON_EXCHANGE: false, // If true, the category can be changed on exchange
  SHOW_INSTALLMENTS_INTEREST: false, // If true, the interest will be shown in the installments
  USE_PAYMENT_GATEWAY_INSTALLMENT_DATA: false, // If true, uses payment gateway (MercadoPago) installment data instead of platform-purchase calculations
  SHOW_TRIP_DETAIL_CARD: false, // If true shows detail card on purchase sidebar for non flat-style tennats
  SHOW_TICKETS_FROM_FRAGMENTS: false, // If true, it calculates and shows tickets from departure/return fragments
  PRICE_ALERTS_ENABLED: false, // If true, the price alerts feature will be enabled
  SHOW_PURCHASER_FORM_BILLING_TITLE: false, // If true, the billing title will be shown in the purchaser form
  GROUP_INCLUDED_FEES: false, // If true, the included fees will be grouped in the purchase sidebar
  ENABLE_CITY_ID_IN_SEARCH: false, // If true, enables the use of city IDs in search url generation
  ENABLE_INVOICE_IN_CHECKOUT: false, // If true, displays toggle to show fields to make a request to get the legal name based on the taxpayer ID
  ENABLE_THIRD_PARTY_INVOICE: false, // If true, replaces the taxpayer-ID invoice input with the self/third-party recipient selector (provider-emitted invoice, e.g. FlotaOspina / TM Solutions). Requires ENABLE_INVOICE_IN_CHECKOUT and a purchaser tributaryNumber document type
  DEFAULT_DECK: 'second' as 'first' | 'second', // Sets the default floor for seat selection ('first' or 'second').
  ALLOW_DUPLICATED_PASSENGERS: false, // If true, allows duplicate passenger names and document ID (e.g., for pets on CDS)
  MINOR_RULES: false, // If true, enables fields and disclaimers related to minor travel mode.
  INFANTS_DISCLAIMER_PER_PASSENGER: false, // If true, shows an infant disclaimer for each passenger
  LAP_CHILD_PER_PASSENGER: false, // If true, allows each adult passenger to declare a lap child (criança de colo) in passenger.meta.lap_child
  LAP_CHILD_MAX_AGE: 0 as number | null, // Max age (exclusive) for a lap child / criança de colo. null or 0 = no limit; when set (e.g. Unida = 6) the lap child must be strictly under this age (age >= value is rejected)
  FREE_FARE_ATTENTION: false, // If true, shows an attention banner on purchase complete when a passenger has a free-fare category (e.g. INSEN/INAPAM), indicating they must validate at the physical counter
  FREE_FARE_DEFAULT_CPF: false, // If true, auto-selects CPF as the document type for non-autofilled passengers
  FREE_FARE_BRAND_GLYPHS: false, // If true, uses brand-specific passenger glyph icons in passenger pickers and tickets
  DISPLAY_FROM_PRICE_LABEL: false, // If true, the "from" label will be displayed next to SimpleTripResult
  MINOR_VALIDATION: false, // If true, enables the minor validation on passengers form
  MINOR_NEEDS_ADULT_COMPANION: false, // If true, a minor (under 13) requires an adult (18+) in the reservation; teens (13-17) may travel alone. The authoritative rule lives in platform-purchase (MINOR_COMPANION_MAX_AGE), which covers every client; this is UX only
  MINOR_ALLOWED_DOCUMENT_TYPES: [] as string[], // Document types a passenger under 18 may hold (e.g. ['TI', 'RC']): a Colombian Cédula de Ciudadanía is only issued at 18. Served by the backend from the same config row it validates against, so the picker and the gate cannot disagree. Empty (default) imposes no restriction
  YOUNG_CHILD_ALLOWED_DOCUMENT_TYPES: [] as string[], // Document types a passenger aged 0-6 may hold (e.g. ['RC','PPN']): a Tarjeta de Identidad is only issued at 7. Unset falls back to MINOR_ALLOWED_DOCUMENT_TYPES, which is today's behaviour. It is narrowed to that row at runtime and a mismatch is warned about once: a 0-6 passenger is a minor, so platform-purchase validates their document against the minor row whatever this one says
  ADULT_ALLOWED_DOCUMENT_TYPES: [] as string[], // Document types a passenger 18+ may hold (e.g. ['CC','RC','PPN']). Empty (default) imposes no restriction, which is what adults have always had. No fallback to MINOR_ALLOWED_DOCUMENT_TYPES: that list exists to exclude the Cédula, which is the document most adults carry
  SHOW_LEGAL_TERMS_CHECKBOX: false, // If true, REQUIRED legal terms checkbox
  SHOW_PROMOTIONAL_TERMS_CHECKBOX: false, // if true optional promotional terms checkbox will be rendered on checkout
  PROMOTIONAL_TERMS_BY_DEFAULT: true, // If true, promotional terms checkbox starts checked; if false, unchecked (e.g. CDS)
  USE_CUSTOMIZE_COOKIES_BANNER: false, // If true, shows the customized cookies banner copy with inline privacy link
  HIDE_PASSENGER_CATEGORY_COPIES: false, // If set to true, the passenger category copy will be hidden.
  SECOND_LAST_NAME_REQUIRED_FOR_DNI: false, // If true, requires the second last name field when the document type is DNI
  DISPLAY_ALL_AVAILABLE_DOCUMENT_TYPES_PER_PASSENGER: false, // If true, shows all document types available to passengers.
  SHOW_DESTINATION_ANIMATION: false, // If true, enables animation to highlight destination in DepartureTripPickedFlat
  AUTOFILL_ENABLED_DEFAULT: false, // If true, autofill for purchaser fields is enabled by default.
  PRICE_CHANGED_MODAL_LOWER_PRICE_ENABLED: false, // If true, the price changed modal will be shown also when the price is lowered
  SHOW_NEXT_DEPARTURES: false, // If true, shows the next departures times on itinerary and details.
  SHOW_DEFAULT_AMENITIES: true, // If true, shows default amenities on results page.
  PURCHASER_NAME_MAX_LENGTH: 100, // Maximum length for purchaser name fields
  PURCHASER_LAST_NAME_MAX_LENGTH: 100, // Maximum length for purchaser last name fields
  PURCHASER_SECOND_LAST_NAME_MAX_LENGTH: 100, // Maximum length for purchaser second last name fields
  SHOW_DURATION_ON_TRIP_DETAILS: false, // If true, shows the trip duration on trip details
  SHOW_FREQUENT_PASSENGERS: false, // If true, shows the frequent passengers on the passengers form
  CDS_FREQUENT_PASSENGER_ENABLED: false, // If true, enables Cruz del Sur Pasajero Frecuente loyalty account (CDS tenant only)
  PURCHASER_EMAIL_MAX_LENGTH: 100, // Maximum length for purchaser email fields
  SHOW_PAYMENT_DISCLAIMER: false, // If true, shows the payment disclaimer on the payment selector
  OXXO_SEAT_RESERVATION_WARNING: false, // If true, shows the OXXO seat reservation time limit warning
  SHOW_CANCEL_TICKET_BUTTON: true, // If true, frontend will show cancellations button
  SHOW_CANCEL_CONFIRMATION_DISCLAIMER: false, // If true, shows CDS cancel modal with penalty/refund/Yape details
  SHOW_EXCHANGE_TICKET_BUTTON: true, // If true, frontend will show exchange button
  SHOW_RESCHEDULE_TICKET_BUTTON: false, // If true, frontend will show reschedule button
  SHOW_BILLING_BUTTON: false, // If true, frontend will show billing button
  SHOW_WOMEN_ONLY_SEAT_FILTER: true, // If true, enables the filter for women-only seats (pink seats) in bus trips.
  REGISTER_USER_AGENT_PROPS_INTO_MIXPANEL: false, // If true, registers platform-specific properties from user agent into Mixpanel events
  SHOW_PURCHASER_ADDRESS_FIELDS: false, // If true, enables the address fields on the purchaser form
  ENABLE_PASSENGER_CITY_SELECTOR: false, // If true, shows the per-passenger address + master-city selector (e.g. Flota Ospina)
  ENABLE_USER_AUTOFILL_BY_DNI: false, // If true, enables autofill of user fields when DNI and document type are provided.
  SHOW_DOCUMENT_TYPE_FIELD_AT: 'end' as 'start' | 'end', // start | end
  ZIP_CODE_NUMERIC_ONLY: false, // If true, zip code field only accepts numeric values, otherwise accepts alphanumeric
  USE_FLAT_FARE_AS_RESULT_PRICE: false, // If true, uses flat fare as result price instead of other discounts
  SHOW_PASSENGER_AUTOFILL_REMINDER: false, // If true, displays a reminder to review passenger details when autofilled.
  USE_SEAT_BASED_PRICING: false,
  USE_TOTAL_FOR_TICKET_PRICING: false, // If true, uses 'total' property for ticket pricing calculations. If false, uses 'amount' (original behavior)
  // Presents the purchase-detail passenger block on the provider's fare basis instead of the
  // platform's. The trip section row shows the sum of window (office) fares, each passenger row
  // shows what that passenger was charged, and a single discount row bridges them.
  //
  // For Rapido Ochoa that reads 190.000 / -74.000 / 116.000 — the figures on its e-ticket and in
  // its sales table — where the platform basis reads 151.000 / -35.000 / 116.000. Both are
  // correct; they differ in which fare the discount is measured against, and only one matches
  // the carrier's books.
  //
  // Only meaningful for a brand whose provider publishes a counter price it actually sells at,
  // so turning it on alone changes nothing: the ticket also has to carry an `officePrice`, which
  // only tenants on platform-purchase's `TENANTS_WITH_OFFICE_PRICE_SNAPSHOT` capture at lock.
  USE_CARRIER_FARE_BASIS: false, // If true, shows the carrier's window fare and its own discount (MID-642)
  WHATSAPP_FAILED_PAYMENT_BUTTON: false, // If true, shows a WhatsApp button on the failed payment screen
  SHOW_CIELO_PIX_WARNING_ON_EXCHANGE: false, // If true, shows a warning about the PIX payment on the exchange ticket screen
  USE_NEW_INSURANCE_TOGGLE_PER_PASSENGER: false, // If true, uses a new insurance toggle instead of a checkbox for insurance per passenger
  SHOW_BOTTOM_TEXT_EXTENDED: false, // If true, shows the bottom text extended on the exchange ticket list.
  SERVICE_LEVELS_ENABLED: false, // If true, shows the service levels on the purchase sidebar
  TP_FUND_WIDGET_ENABLED: false, // If true, enables the TP Fund Widget integration
  INVOICE_URL_TOKEN: false, // If true, enables the use of a token in the invoice URL at checkout
  SHOW_MOBILE_TOPBAR_LOGO: false, // If true, shows the regular topbar logo on mobile
  USE_OTP_FOR_WALLET: false, // If true, uses OTP for wallet payment
  OPT_CONTACT_METHOD_AT_REDEEM_POINTS: 'both' as 'sms' | 'email' | 'both', // sms | email | both This is to allow the use of both methods for the redeem points confirmation
  DEEP_EMAIL_VALIDATION: false, // If true, enables the use of deep email validation
  PASSENGER_INSURANCE_BY_DEFAULT: false, // If true, insurance will be selected by default for passengers. OBS: This only applies for per passenger / trip insurance
  ALLOW_COMPLETE_CANCELLATION: false, // If true, allows complete cancellation of tickets
  EXCHANGE_HIDE_APPLIED_INSURANCE: false, // If true, hides the applied insurance on the exchange ticket list.
  SHOW_REFUND_PENDING_DISCLAIMER: false, // If true, shows the refund pending disclaimer on the exchange ticket section.
  HIDE_NAVIGATION_MIN_PRICES: false, // If true, hides the min prices on the navigation buttons on search results
  SHOW_DOWNLOAD_APP_MODAL: false, // If true, shows the download app modal on search results.
  SHOW_RESERVE_CODE: false, // If true, shows the reserve code on the purchase tickets section.
  SHOW_TERMINALS_ON_RESULTS: false, // If true, shows the terminals on the trip results. But it should be enabled with caution, because it can break some layouts.
  EXCHANGE_ALLOW_PASSENGER_ADJACENT_SEAT: true, // Allows exchanging the passenger's main seat.
  EXCHANGE_ALLOW_ADJACENT_SEAT: true, // Allows exchanging the adjacent seat in a bundled booking.
  SHOW_CURRENCY_SUFFIX: true, // If true, shows currency code suffix in formatted currency strings
  PAYPAL_V6_ENABLED: false, // If true, enables PayPal v6 SDK with modal experience instead of legacy redirect flow
  WALLET_SLIDER_INPUT_ENABLED: false, // If true, shows an input to type the amount to use in the wallet points slider. Applies only for wallets (Travelpass)
  SHOW_DISCOUNT_PRICE_COLUMN: true, // If true, shows the price/not_available column in discount row items
  CUSTOM_MANAGEMENT_COSTS_LABEL: false, // If true, shows the custom management costs label on the purchase sidebar.
  MANAGEMENT_COSTS_TOOLTIP: false, // If true, shows a tooltip with info about the convenience fee on the purchase pricing breakdown.
  SHOW_INSURANCE_DECIMALS: false, // If true, always shows 2 decimals for insurance prices, even when the price is a whole number (e.g., 6.00 shows as "R$ 6,00" instead of "R$ 6")
  EXCHANGE_VALIDATE_ELIGIBILITY_BEFORE_SCHEDULE: false, // If true, validates the eligibility of the exchange ticket before scheduling it.
  SHOW_INSURANCE_AMOUNT_WITH_TAXES: false, // If true, shows insurance price with taxes included and displays "(impuestos incluidos)" label. When false, shows base price with "+ impuestos" legend only if taxRate > 0
  HIDE_CATEGORY_AVAILABILITY_ON_RESULTS: false, // If true, hides the category availability badges (e.g., "21 INSEN, 21 PCD") on search results
  HIDE_CATEGORY_AVAILABILITY_COUNT_ON_RESULTS: false, // If true, category availability badges on search results show only the category name (e.g., "INSEN") without the seat count
  HIDE_CACHED_TRIPS_DURING_POLLING: false, // If true, shows full loading screen while polling. If false, shows cached trips with a small loader at the top of the results.
  LINE_SPECIFIC_TERMS_ENABLED: false, // If true, shows the line specific terms on the passengers form.
  HIDE_RESERHUB_LOGIN_ON_WEBVIEW: false, // If true, hides the Reserhub login button when the app is in a webview
  // If true, resolves the Reserhub unified account inside the native app WebView. Required because the
  // native_app_* user-agent tag is shared across saas-app and midsize-app, so without an opt-in every
  // app would start calling GET /accounts/auth/me. Do NOT enable without HIDE_RESERHUB_LOGIN_ON_WEBVIEW,
  // or the web account menu renders inside the app.
  RESERHUB_ACCOUNT_ON_NATIVE_APP_ENABLED: false,
  CDS_ACCOUNT_WEBVIEW_ENABLED: false,
  FREE_FARE_BENEFIT_DENIAL: false, // If true, shows the benefit-denial modal when the searched passenger category has no availability
  SHOW_DOCUMENT_VALIDATION_DISCLAIMER: false, // If true, shows the document validation disclaimer on the file select field.
  PRODUCTS_CART_ENABLED: false, // Fallback for the products cart (snacks, beverages, etc.) when the GrowthBook flag `products_cart_enabled_{brand}` is absent. The gate resolves GrowthBook first, then this value.
  PRODUCTS_MAX_QUANTITY_PER_ITEM: 3, // Max units of a single product code a passenger can select, per segment. Does not limit how many distinct products are chosen. Combines with stock as min(availableStock, this value).
  WELCOME_DIGITAL_DISCOUNT_ENABLED: false, // If true, shows the welcome digital discount modal on the search results page and on the purchase sidebar.
  WELCOME_DIGITAL_DISCOUNT_ROUTES: [] as { origin: string; destination: string }[], // Array of { origin: string, destination: string } objects. If empty, shows on all routes. If populated, only shows on matching routes (uses cityName for comparison).
  BUS_TRACKER_ENABLED: false, // If true, shows a bus tracker image on the purchase complete screen
  BUS_TRACKER_URL: '', // URL for the bus tracker link on the purchase complete screen
  ACCOMMODATION_PROMO_ENABLED: false, // If true, shows the accommodation promo banner on the purchase complete screen
  DISCOUNT_CODE_SKIP_ON_EXISTING_DISCOUNT: false, // If true, skips the discount code input when an existing_discount (e.g., advance purchase via hasDynamicPricing) is already applied. The coupon feature remains enabled, but the input is hidden when there's already a discount.
  PURCHASE_FLOW_TERMINAL_DISPLAY: 'default' as 'default' | 'city_terminal', // ('default' | 'city_terminal') It normalizes how the terminal information is displayed throughout the purchase process
  HIDE_DIRECT_LABEL_WITH_NO_STOPS: false, // If true, hides "Directo" label and replaces "escala/escalas" with "conexão/conexões" (Portuguese)
  FORCE_LANGUAGE_DROPDOWN: false, // If true, forces the language dropdown to be shown even when only two languages are available
  HIDE_CATEGORIES_NOT_AVAILABLE_INFO: false, // If true, hides the "categories not available" info text when only one category is available
  ALT_COUNTRY_CODE_LABEL: false, // If true, formats country selector options as "+52 México" instead of "México +52".
  // If true, the push campaign parameters captured at bundle start (utm_campaign, utm_source,
  // utm_medium, utm_id, utm_content) travel in the purchase creation payload, so the Mixpanel
  // purchase events platform-purchase emits later can carry them. When false the payload is
  // unchanged and nothing is captured.
  SEND_CAMPAIGN_ATTRIBUTION_TO_PURCHASE: false,
};

type WhitelabelFeatures = typeof defaultFeatures;

export type { WhitelabelFeatures };
export { defaultFeatures };
